import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { X, FileText, Printer } from 'lucide-react';
import { safePrintWindow, escapeHtml } from '../utils/printUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalCertificateModal = ({ patient, token, onClose }) => {
  const [diagnosis, setDiagnosis] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedCertificate, setGeneratedCertificate] = useState(null);
  const [manualEntry, setManualEntry] = useState(!patient);
  const [manualName, setManualName] = useState('');
  const [manualAge, setManualAge] = useState('');
  const [manualSex, setManualSex] = useState('');

  const handleGenerate = async () => {
    if (!diagnosis.trim() || !fromDate || !toDate) {
      toast.error('Please fill diagnosis and date range');
      return;
    }

    if (manualEntry && (!manualName.trim() || !manualAge || !manualSex)) {
      toast.error('Please fill all patient details');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        diagnosis,
        from_date: fromDate,
        to_date: toDate,
        remarks
      };

      if (manualEntry) {
        payload.patient_name = manualName;
        payload.patient_age = manualAge.toString();
        payload.patient_sex = manualSex;
      } else {
        payload.patient_id = patient.id;
      }

      const response = await axios.post(
        `${API}/medical-certificates`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedCertificate(response.data);
      toast.success('Medical certificate generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!generatedCertificate) return;
    const c = generatedCertificate;
    const styles = `* { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; padding: 15mm; background: white; }
      .certificate { border: 2px solid #000; padding: 20mm; min-height: 250mm; position: relative; }
      .header { text-align: center; margin-bottom: 20px; }
      .header h1 { font-size: 28pt; margin-bottom: 5px; }
      .header p { font-size: 14pt; }
      .header .subtitle { font-size: 12pt; color: #666; margin-top: 10px; }
      .date { text-align: right; color: #666; margin-bottom: 15px; }
      .patient-info p { margin: 5px 0; font-size: 12pt; }
      .diagnosis-box { background: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0; }
      .rest-period { background: #e3f2fd; padding: 15px; border-radius: 5px; margin: 20px 0; }
      .certify-text { font-size: 12pt; margin: 15px 0; }
      .remarks { margin-top: 15px; }
      .signature { position: absolute; bottom: 20mm; right: 20mm; text-align: right; }
      .signature .line { border-top: 1px solid #000; width: 150px; margin-bottom: 5px; margin-left: auto; }
      .signature .label { font-size: 10pt; color: #666; }
      .signature .name { font-weight: bold; font-size: 12pt; }
      .signature .qual { color: #666; font-size: 11pt; }
      .signature .clinic { font-size: 10pt; color: #666; margin-top: 5px; }
      @media print { @page { size: A4; margin: 10mm; } body { padding: 0; } }`;
    const body = `<div class="certificate">
      <div class="header"><h1>K.M</h1><p>Multi-Speciality Clinic, Koliyanur</p><p class="subtitle">Medical Certificate</p></div>
      <p class="date">Date: ${new Date(c.issued_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
      <div class="patient-info"><p><strong>Patient Name:</strong> ${escapeHtml(c.patient_name)}</p>
        ${c.token_number ? `<p><strong>Token No:</strong> ${escapeHtml(c.token_number)}</p>` : ''}
        <p><strong>Age/Sex:</strong> ${escapeHtml(c.patient_age)} / ${escapeHtml(c.patient_sex)}</p></div>
      <div class="diagnosis-box"><p><strong>Diagnosis:</strong></p><p>${escapeHtml(c.diagnosis)}</p></div>
      <p class="certify-text">This is to certify that the above-named patient has been examined and advised rest from duty/work for medical reasons.</p>
      <div class="rest-period"><p><strong>Rest Period:</strong></p>
        <p><strong>From:</strong> ${new Date(c.from_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        <p><strong>To:</strong> ${new Date(c.to_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p></div>
      ${c.remarks ? `<div class="remarks"><p><strong>Remarks:</strong></p><p>${escapeHtml(c.remarks)}</p></div>` : ''}
      <div class="signature"><div class="line"></div><p class="label">Signature</p>
        <p class="name">${escapeHtml(c.doctor_name)}</p><p class="qual">${escapeHtml(c.doctor_qualification)}</p>
        <p class="clinic">K.M. Multi Speciality Clinic, Koliyanur</p></div>
    </div>`;
    safePrintWindow('Medical Certificate - K.M', `<style>${styles}</style>${body}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print" onClick={onClose}>
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto print-content-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 print:p-8">
          <div className="flex items-center justify-between mb-6 print:hidden">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold">Medical Certificate</h2>
            </div>
            <Button onClick={onClose} variant="ghost" size="icon">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {!generatedCertificate ? (
            <div className="space-y-4">
              {patient ? (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Patient: {patient.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Token: {patient.token_number} • Age: {patient.age} • Sex: {patient.sex}
                      </p>
                    </div>
                    <Button
                      onClick={() => setManualEntry(!manualEntry)}
                      variant="outline"
                      size="sm"
                    >
                      {manualEntry ? 'Use Token Patient' : 'Manual Entry'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Manual patient entry mode</p>
              )}

              {manualEntry && (
                <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold">Patient Details (Manual Entry)</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Patient Name"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      data-testid="manual-name-input"
                    />
                    <Input
                      type="number"
                      placeholder="Age"
                      value={manualAge}
                      onChange={(e) => setManualAge(e.target.value)}
                      data-testid="manual-age-input"
                    />
                    <select
                      value={manualSex}
                      onChange={(e) => setManualSex(e.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                      data-testid="manual-sex-select"
                    >
                      <option value="">Sex</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="Enter diagnosis"
                  data-testid="diagnosis-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="fromDate">From Date</Label>
                  <Input
                    id="fromDate"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    data-testid="from-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="toDate">To Date</Label>
                  <Input
                    id="toDate"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    data-testid="to-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks (Optional)</Label>
                <textarea
                  id="remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Additional remarks"
                  className="w-full min-h-[80px] p-3 border border-input rounded-md"
                  data-testid="remarks-input"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleGenerate} disabled={loading} className="flex-1" data-testid="generate-cert-button">
                  {loading ? 'Generating...' : 'Generate Certificate'}
                </Button>
                <Button onClick={onClose} variant="outline">Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="print:hidden mb-4 flex gap-2">
                <Button onClick={handlePrint} className="flex-1">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Certificate
                </Button>
                <Button onClick={onClose} variant="outline">Close</Button>
              </div>

              {/* Certificate Content - Responsive A4 Format */}
              <div id="certificate-print-content" className="border-2 border-primary p-4 sm:p-6 md:p-8 rounded-lg bg-white">
                <div className="text-center mb-6 sm:mb-8">
                  <h1 className="text-2xl sm:text-3xl font-bold text-primary">K.M</h1>
                  <p className="text-base sm:text-lg font-semibold">Multi-Speciality Clinic, Koliyanur</p>
                  <p className="text-sm text-muted-foreground mt-2">Medical Certificate</p>
                </div>

                <div className="space-y-3 sm:space-y-4 text-sm sm:text-base">
                  <p className="text-right text-muted-foreground">
                    Date: {new Date(generatedCertificate.issued_date).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>

                  <div className="space-y-1 sm:space-y-2 patient-info">
                    <p><strong>Patient Name:</strong> {generatedCertificate.patient_name}</p>
                    {generatedCertificate.token_number && (
                      <p><strong>Token No:</strong> {generatedCertificate.token_number}</p>
                    )}
                    <p><strong>Age/Sex:</strong> {generatedCertificate.patient_age} / {generatedCertificate.patient_sex}</p>
                  </div>

                  <div className="my-4 sm:my-6 p-3 sm:p-4 bg-slate-50 rounded diagnosis-section">
                    <p className="mb-2"><strong>Diagnosis:</strong></p>
                    <p>{generatedCertificate.diagnosis}</p>
                  </div>

                  <p className="text-sm sm:text-base">
                    This is to certify that the above-named patient has been examined and advised rest from duty/work for medical reasons.
                  </p>

                  <div className="my-3 sm:my-4 p-3 sm:p-4 bg-blue-50 rounded">
                    <p className="font-semibold mb-2">Rest Period:</p>
                    <p className="text-sm sm:text-base">
                      <strong>From:</strong> {new Date(generatedCertificate.from_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-sm sm:text-base">
                      <strong>To:</strong> {new Date(generatedCertificate.to_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>

                  {generatedCertificate.remarks && (
                    <div className="mt-3 sm:mt-4">
                      <p><strong>Remarks:</strong></p>
                      <p>{generatedCertificate.remarks}</p>
                    </div>
                  )}

                  {/* Signature - Always Bottom Right */}
                  <div className="mt-10 sm:mt-16 pt-4 sm:pt-6 border-t flex justify-end signature-section">
                    <div className="text-right">
                      <div className="mb-6 sm:mb-8">
                        <p className="text-muted-foreground">____________________</p>
                        <p className="text-xs text-muted-foreground">Signature</p>
                      </div>
                      <p className="font-semibold">{generatedCertificate.doctor_name}</p>
                      <p className="text-muted-foreground text-sm">{generatedCertificate.doctor_qualification}</p>
                      <p className="text-xs text-muted-foreground mt-2">K.M. Multi Speciality Clinic, Koliyanur</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default MedicalCertificateModal;
